import type { RosterItemResponse } from '@on3/api';
import { useSite } from '@on3/ui-lib/index';
import clsx from 'clsx';
import { type ISortFilterItem } from 'components/Db/Filters/SortFilter';
import { ArrowRight } from 'components/Svg/ArrowRight';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import { useCallback } from 'react';

import styles from './HighSchoolRosterHeader.module.scss';

interface IHighSchoolHeadersProps {
  selected?: keyof RosterItemResponse | string;
  onChange?: React.MouseEventHandler<HTMLButtonElement>;
  ascending?: boolean;
  primaryColor?: string | null;
}

export const HighSchoolHeaderList: ISortFilterItem[] = [
  {
    key: 1,
    name: 'jerseyNumber',
    displayName: '#',
    flipDirection: false,
  },
  { key: 2, name: 'lastName', displayName: 'Player', flipDirection: false },
  {
    key: 3,
    name: 'position',
    displayName: 'Pos',
    flipDirection: false,
  },
  {
    key: 4,
    name: 'height',
    displayName: 'Ht',
    flipDirection: true,
    dropDownOnly: true,
  },
  {
    key: 5,
    name: 'weight',
    displayName: 'Wt',
    flipDirection: true,
    dropDownOnly: true,
  },
  {
    key: 6,
    name: 'rating',
    displayName: 'Rating',
    flipDirection: true,
  },
  { key: 7, name: 'nilValue', displayName: 'NIL Value', flipDirection: true },
  { key: 8, name: 'status', displayName: 'Status', flipDirection: false },
  {
    key: 9,
    name: 'athleteVerified',
    displayName: 'Athlete-Verified',
    flipDirection: false,
  },
];

export const HighSchoolRosterHeader = ({
  selected,
  onChange,
  ascending,
  primaryColor,
}: IHighSchoolHeadersProps) => {
  const { currentSite } = useSite();
  const themeColor = primaryColor || currentSite?.primaryColor || '#fe3b1f';
  const buttonStyles = useCallback(
    (isActive: boolean) => ({ borderColor: isActive ? themeColor : '#e4e8f2' }),
    [themeColor],
  );
  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      onChange?.(e);
    },
    [onChange],
  );

  return (
    <header>
      <ul className={styles.headerListContainer}>
        {HighSchoolHeaderList.map((item) => {
          const isSelected = item.name === selected;

          if (item?.dropDownOnly) {
            return null;
          }

          return (
            <li className={styles.listItem} key={item.key}>
              <PrimaryButton
                className={styles.btnSort}
                data-flip={item.flipDirection}
                data-sorttype={item.name}
                endIcon={
                  selected === item.name && (
                    <ArrowRight
                      className={clsx(styles.arrow, {
                        [styles.flip]: item.flipDirection
                          ? !ascending
                          : ascending,
                      })}
                      stroke={themeColor}
                    />
                  )
                }
                onClick={handleClick}
                style={buttonStyles(isSelected)}
                variant="text"
              >
                <Text
                  className={styles.title}
                  component="h4"
                  variant="subtitle2"
                >
                  {item.displayName}
                </Text>
              </PrimaryButton>
            </li>
          );
        })}
      </ul>
    </header>
  );
};
